import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../../shared/lib/firebase';
import { useAuthState } from '../auth/AuthContext';
import { promptEmailVerification } from '../auth/EmailVerificationNotice';

interface FavoritesContextValue {
  favoriteIds: string[];
  isFavorite: (id: string) => boolean;
  toggleFavorite: (id: string) => Promise<void>;
  loading: boolean;
}

const FavoritesContext = createContext<FavoritesContextValue | undefined>(undefined);

export function FavoritesProvider({ children }: { children: ReactNode }) {
  const { user, emailVerified } = useAuthState();
  const emailVerificationBlocked = user?.verificationRequired === true && !emailVerified;
  const [favoriteIds, setFavoriteIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  // Load favorites from Firestore when user logs in
  useEffect(() => {
    if (!user) {
      setFavoriteIds([]);
      return;
    }
    setLoading(true);
    const userRef = doc(db, 'users', user.uid);
    getDoc(userRef)
      .then((snap) => {
        if (snap.exists()) {
          setFavoriteIds(snap.data().favoritePropertyIds ?? []);
        } else {
          setFavoriteIds([]);
        }
      })
      .catch(() => setFavoriteIds([]))
      .finally(() => setLoading(false));
  }, [user]);

  const toggleFavorite = useCallback(async (id: string) => {
    if (!user) return;
    if (emailVerificationBlocked) {
      promptEmailVerification();
      return;
    }
    const next = favoriteIds.includes(id)
      ? favoriteIds.filter((fid) => fid !== id)
      : [...favoriteIds, id];
    setFavoriteIds(next);
    const userRef = doc(db, 'users', user.uid);
    try {
      await updateDoc(userRef, { favoritePropertyIds: next });
    } catch {
      // Document may not exist yet; create it
      await setDoc(userRef, { favoritePropertyIds: next }, { merge: true });
    }
  }, [user, favoriteIds, emailVerificationBlocked]);

  const isFavorite = useCallback((id: string) => favoriteIds.includes(id), [favoriteIds]);

  return (
    <FavoritesContext.Provider value={{ favoriteIds, isFavorite, toggleFavorite, loading }}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites(): FavoritesContextValue {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error('useFavorites must be used within FavoritesProvider');
  return ctx;
}
